import React, { useEffect, useState } from 'react';
import axios from 'axios';

/**
 * SmartAutoLocationDetection - Permission-Aware Version
 * 
 * ✅ Checks permission status BEFORE asking
 * ✅ Only prompts user when necessary
 * ✅ Shows UI feedback during process
 * ✅ Handles all permission states
 */
const AutoLocationDetection = ({ 
  onLocationDetected, 
  onPermissionStatusChange,
  debug = false,
  autoTrigger = false
}) => {
  const [permissionStatus, setPermissionStatus] = useState('prompt');
  const [isDetecting, setIsDetecting] = useState(false);

  const log = (message, data) => {
    if (debug) {
    }
  };

  // ✅ FIX 1: Load saved location on mount + check permission
  useEffect(() => {
    const hasSaved = loadSavedLocation();
    if (!hasSaved) {
      checkPermissionStatus();
    }
  }, []);

  // ✅ FIX 2: Auto-trigger for both 'granted' and 'prompt' states
  useEffect(() => {
    if (autoTrigger && (permissionStatus === 'granted' || permissionStatus === 'prompt')) {
      log('Auto-triggering location detection');
      detectLocation();
    }
  }, [autoTrigger, permissionStatus]);

  const checkPermissionStatus = async () => {
    try {
      if (!navigator.permissions || !navigator.permissions.query) {
        log('⚠️ Permissions API not supported, will use fallback');
        setPermissionStatus('prompt');
        if (onPermissionStatusChange) {
          onPermissionStatusChange('prompt');
        }
        return;
      }

      const result = await navigator.permissions.query({ name: 'geolocation' });
      
      log('📍 Permission status:', result.state);
      setPermissionStatus(result.state);
      
      if (onPermissionStatusChange) {
        onPermissionStatusChange(result.state);
      }

      result.addEventListener('change', () => {
        log('📍 Permission changed to:', result.state);
        setPermissionStatus(result.state);
        if (onPermissionStatusChange) {
          onPermissionStatusChange(result.state);
        }
      });

    } catch (error) {
      log('⚠️ Could not check permission status:', error.message);
      setPermissionStatus('prompt');
      if (onPermissionStatusChange) {
        onPermissionStatusChange('prompt');
      }
    }
  };

  const detectLocation = async () => {
    if (isDetecting) {
      log('⚠️ Detection already in progress');
      return;
    }

    setIsDetecting(true);
    log('🚀 Starting location detection...');

    try {
      const position = await getCurrentPosition();
      const { latitude, longitude } = position.coords;
      
      log('✅ Coordinates obtained:', { latitude, longitude });
      
      setPermissionStatus('granted');
      if (onPermissionStatusChange) {
        onPermissionStatusChange('granted');
      }
      
      const locationDetails = await reverseGeocode(latitude, longitude);
      
      const detectedData = {
        coordinates: [latitude, longitude],
        details: locationDetails,
        timestamp: new Date().toISOString()
      };
      
      log('✅ Location detected:', locationDetails);
      
      try {
        localStorage.setItem('userLocation', JSON.stringify(detectedData));
        log('💾 Location saved to localStorage');
      } catch (e) {
        log('⚠️ Could not save to localStorage:', e.message);
      }
      
      if (onLocationDetected) {
        onLocationDetected(detectedData);
      }
      
      setIsDetecting(false);
      
    } catch (error) {
      log('❌ Location detection failed:', error.message);
      
      if (error.message === "Permission denied") {
        setPermissionStatus('denied');
        if (onPermissionStatusChange) {
          onPermissionStatusChange('denied');
        }
      }
      
      if (onLocationDetected) {
        onLocationDetected(null);
      }
      
      setIsDetecting(false);
    }
  };

  const loadSavedLocation = () => {
    try {
      const saved = localStorage.getItem('userLocation');
      if (saved) {
        const locationData = JSON.parse(saved);
        
        const savedDate = new Date(locationData.timestamp);
        const now = new Date();
        const daysDiff = (now - savedDate) / (1000 * 60 * 60 * 24);
        
        if (daysDiff < 7) {
          log('📦 Loading saved location:', locationData.details);
          if (onLocationDetected) {
            onLocationDetected(locationData);
          }
          return true;
        } else {
          log('⏰ Saved location is too old, will request fresh data');
          localStorage.removeItem('userLocation');
        }
      }
    } catch (e) {
      log('⚠️ Could not load saved location:', e.message);
    }
    return false;
  };

  const clearSavedLocation = () => {
    try {
      localStorage.removeItem('userLocation');
      log('🗑️ Cleared saved location');
    } catch (e) {
      log('⚠️ Could not clear saved location:', e.message);
    }
  };

  const getCurrentPosition = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation not supported"));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        position => resolve(position),
        error => {
          let errorMessage;
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = "Permission denied";
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = "Position unavailable";
              break;
            case error.TIMEOUT:
              errorMessage = "Request timeout";
              break;
            default:
              errorMessage = "Unknown error";
          }
          reject(new Error(errorMessage));
        },
        { 
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 300000
        }
      );
    });
  };

  const reverseGeocode = async (latitude, longitude) => {
    try {
      const response = await axios.get(
        `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`,
        { timeout: 8000 }
      );
      
      return {
        city: response.data.locality || response.data.city || 'Unknown',
        state: response.data.principalSubdivision || '',
        country: response.data.countryName || 'Unknown',
        countryCode: response.data.countryCode || 'US',
        formattedAddress: `${response.data.locality || response.data.city || 'Unknown'}, ${response.data.principalSubdivision || ''}, ${response.data.countryName || 'Unknown'}`
      };
    } catch (error) {
      log('❌ Reverse geocoding failed:', error.message);
      throw new Error("Failed to get location details");
    }
  };

  React.useImperativeHandle(React.useRef(), () => ({
    detectLocation,
    checkPermissionStatus,
    loadSavedLocation,
    clearSavedLocation,
    permissionStatus,
    isDetecting
  }));

  return null;
};

AutoLocationDetection.detectLocation = function(component) {
  if (component && component.detectLocation) {
    component.detectLocation();
  }
};

export default AutoLocationDetection;